import { useEffect, useRef, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { cn } from '@worldcoo/ui/lib/utils';
import { useNgoAreaUrl } from '@/lib/ngo-area';
import { PillLink } from '../primitives';

type Step = {
	/** The big ordinal the card prints in its top-right corner. */
	number: string;
	title: string;
	body: string;
	illustration: string;
	/** Surface + text colours; each step is a different card in the hand-off. */
	surface: string;
	tone: string;
	/** The last card carries a CTA instead of being a step in the process. */
	cta?: string;
};

/**
 * How far through the section we are, as a step index.
 *
 * Driven by the section's own rect rather than a library: the sticky column is pinned for exactly the
 * section's scrollable length, so the fraction of that length already scrolled IS the progress. Read
 * inside `requestAnimationFrame` so a fast wheel does not force a layout per event.
 */
function useScrollStep(steps: number) {
	const ref = useRef<HTMLDivElement>(null);
	const [active, setActive] = useState(0);

	useEffect(() => {
		const element = ref.current;
		if (!element) return;

		// The animation is decoration; a visitor who asked for less motion just gets the first card.
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

		let frame = 0;
		const measure = () => {
			frame = 0;
			const rect = element.getBoundingClientRect();
			// Total distance the sticky child travels: the section's height minus one viewport.
			const travel = rect.height - window.innerHeight;
			if (travel <= 0) return;
			const progress = Math.min(1, Math.max(0, -rect.top / travel));
			// `steps - 1` so the last card is reached exactly at the bottom rather than one step early.
			setActive(Math.round(progress * (steps - 1)));
		};

		const onScroll = () => {
			if (frame) return;
			frame = requestAnimationFrame(measure);
		};

		measure();
		window.addEventListener('scroll', onScroll, { passive: true });
		window.addEventListener('resize', onScroll);
		return () => {
			if (frame) cancelAnimationFrame(frame);
			window.removeEventListener('scroll', onScroll);
			window.removeEventListener('resize', onScroll);
		};
	}, [steps]);

	return { ref, active };
}

/**
 * One card in the deck. The hand-off draws the mobile card at 275x330 and the desktop one at 354x424 —
 * the same card at two scales, which is why every inner size is a breakpoint pair rather than one
 * value in a scaled wrapper (a CSS `scale` would blur the type).
 */
function StepCard({ step, className, style }: { step: Step; className?: string; style?: React.CSSProperties }) {
	return (
		<article
			className={cn(
				'flex h-[330px] w-[275px] flex-col justify-end overflow-hidden rounded-[19px] p-8',
				'lg:h-[424px] lg:w-[354px] lg:rounded-3xl lg:p-10',
				step.surface,
				step.tone,
				className,
			)}
			style={style}
		>
			<img
				src={step.illustration}
				alt=""
				aria-hidden
				className="absolute top-6 left-8 h-auto w-[150px] lg:top-8 lg:left-10 lg:w-[197px]"
			/>
			<span className="absolute top-6 right-6 text-xl leading-[1.5] tracking-[-0.02em] opacity-90 lg:top-8 lg:right-8 lg:text-2xl">
				{step.number}
			</span>
			<div className="relative flex flex-col gap-1">
				<h3 className="text-xl leading-[1.2] font-medium tracking-[-0.02em] lg:text-2xl">{step.title}</h3>
				<p className="text-sm leading-[1.5] lg:text-base">{step.body}</p>
				{step.cta && <span className="mt-3 text-[15px] font-medium tracking-[-0.02em] underline">{step.cta}</span>}
			</div>
		</article>
	);
}

/** The 01–04 progress rail: a hairline track with the active step's segment in brand orange. */
function StepRail({ steps, active }: { steps: Step[]; active: number }) {
	return (
		<div className="flex shrink-0 items-center gap-2 lg:gap-1.5">
			<div className="bg-ink/10 relative h-24 w-0.5 rounded-full lg:h-[122px]">
				<div
					className="bg-brand absolute w-full rounded-full transition-all duration-500"
					style={{ height: `${100 / steps.length}%`, top: `${(active * 100) / steps.length}%` }}
				/>
			</div>
			<ol className="flex flex-col gap-3 lg:gap-2">
				{steps.map((step, index) => (
					<li
						key={step.number}
						className={cn(
							'text-base leading-[1.5] font-medium tracking-[-0.02em] transition-opacity',
							index === active ? 'opacity-100' : 'opacity-20',
						)}
					>
						{step.number}
					</li>
				))}
			</ol>
		</div>
	);
}

/**
 * "¿Cómo empezar a colaborar?" — the four-step onboarding path.
 *
 * Scroll-driven at BOTH breakpoints, which the mobile frame confirms: copy, deck and rail are each
 * their own sticky column there too, simply stacked instead of set side by side. So this is one
 * layout that goes from a row to a column, not two different sections.
 */
export function HowToCollaborate() {
	const { t } = useLingui();
	const ngoArea = useNgoAreaUrl();

	const steps: Step[] = [
		{
			number: '01',
			title: t`Send your application`,
			body: t`Fill in the form with your organisation's basic information.`,
			illustration: '/redesign/partner-ngos/illo-send.svg',
			surface: 'bg-apricot',
			tone: 'text-black',
		},
		{
			number: '02',
			title: t`Assessment`,
			body: t`Our team will check that you meet the minimum transparency criteria. If something is missing, don't worry — we'll tell you what to correct so you can apply again.`,
			illustration: '/redesign/partner-ngos/illo-review.svg',
			surface: 'bg-ink',
			tone: 'text-white',
		},
		{
			number: '03',
			title: t`Agreement and activation`,
			body: t`We sign the collaboration agreement and you're all set!`,
			illustration: '/redesign/partner-ngos/illo-agreement.svg',
			surface: 'bg-brand',
			tone: 'text-black',
		},
		{
			number: '04',
			title: t`Still have questions before you start?`,
			body: t`Join the next live information session.`,
			illustration: '/redesign/partner-ngos/illo-folder.svg',
			surface: 'bg-olive',
			tone: 'text-black',
			cta: t`Register`,
		},
	];

	const { ref, active } = useScrollStep(steps.length);

	return (
		/* One viewport to pin in, plus 70vh of travel per card after the first. A full viewport per card
		   (the obvious `steps * 100vh`) makes the section four screens tall and the page reads as stuck. */
		<section ref={ref} className="relative" style={{ height: `calc(100vh + ${(steps.length - 1) * 70}vh)` }}>
			<div className="sticky top-0 flex h-screen items-center overflow-hidden">
				{/* `gap-12` stacked, not the desktop `gap-10`: the deck's cards are rotated, so they reach past
					    the box that lays them out and a 24px gap left them almost touching the step rail below. */}
				<div className="mx-auto flex w-full max-w-[1440px] flex-col items-center gap-12 px-4 lg:flex-row lg:items-center lg:justify-between lg:gap-10 lg:px-16">
					<div className="flex max-w-[646px] flex-col items-start gap-4">
						<h2 className="text-3xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem]">
							{t`How do you start collaborating?`}
						</h2>
						<p className="text-base leading-[1.5]">
							{t`If you're looking for funding for your social projects, the Solidarity Round-Up and our digital tools connect you with the customers and employees of large companies. Your projects join our database so partner companies can select them according to their values.`}
						</p>
						<PillLink variant="ink" href={ngoArea('/onboarding')}>{t`Register your organisation`}</PillLink>
					</div>

					<div className="relative h-[330px] w-[275px] shrink-0 lg:h-[424px] lg:w-[354px]">
						{steps.map((step, index) => {
							const offset = index - active;
							const isPast = offset < 0;
							return (
								<StepCard
									key={step.number}
									step={step}
									className="absolute inset-0 transition-all duration-500 ease-out"
									style={{
										// Cards ahead of the active one fan out behind it; cards already dealt slide up
										// and fade, so the deck reads as a stack being worked through.
										transform: isPast
											? 'translateY(-32px) scale(0.94) rotate(-6deg)'
											: `translateY(${offset * 12}px) rotate(${offset * 4}deg) scale(${1 - offset * 0.03})`,
										opacity: isPast ? 0 : 1,
										zIndex: steps.length - Math.abs(offset),
										pointerEvents: offset === 0 ? 'auto' : 'none',
									}}
								/>
							);
						})}
					</div>

					<StepRail steps={steps} active={active} />
				</div>
			</div>
		</section>
	);
}
